import { Location } from "@angular/common";
import { Injectable, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { ObservableProps } from "src/mixins/ObservableProps";
import { BrandClient, BrandViewModel } from "src/domain/client";
import themes, { ValidTheme } from "src/domain/themes";

@Injectable({
  providedIn: "root",
})
export class BrandService extends ObservableProps<{
  brand: BrandViewModel | null;
  specialBrand: BrandViewModel | null;
}>
  implements OnDestroy
{
  private brandIdCache = new Map<ValidTheme, number>();
  private brandCache = new Map<number, BrandViewModel>();
  private onDestroySubscription: Subscription;

  constructor(private location: Location) {
    const onDestroySubscription = new Subscription();
    super(
      {
        brand: null,
        specialBrand: null,
      },
      onDestroySubscription,
    );
    this.onDestroySubscription = onDestroySubscription;
  }

  ngOnDestroy(): void {
    this.onDestroySubscription.unsubscribe();
  }

  setSpecialBrand(brand: ValidTheme): void {
    new BrandClient().brand(brand).then((brandViewModel: BrandViewModel) => {
      this.specialBrand = brandViewModel;
    });
  }

  private getCachedBrandById(id: number) {
    return this.brandCache.get(id) ?? null;
  }

  private getCachedBrandByShortURL(brand: ValidTheme) {
    const brandId = this.brandIdCache.get(brand);
    return brandId != null ? this.getCachedBrandById(brandId) : null;
  }

  get brandId() {
    return this.brand?.brandId;
  }

  get specialBrandId() {
    return this.specialBrand?.brandId;
  }

  async getBrand(
    brand: ValidTheme | number,
    forceFresh = false,
  ): Promise<BrandViewModel> {
    if (!forceFresh) {
      let cachedBrand: BrandViewModel | null =
        typeof brand === "number"
          ? this.getCachedBrandById(brand)
          : this.getCachedBrandByShortURL(brand);
      if (cachedBrand) return Promise.resolve(cachedBrand);
    }
    const client = new BrandClient();
    const brandViewModel = await (typeof brand === "number"
      ? client.byId(brand)
      : client.brand(brand));
    this.brand = brandViewModel;
    return brandViewModel;
  }

  getBrandFromURL(): string {
    const url = this.location.path().split("/")[1];

    if (!url) {
      return "";
    }

    if (url.includes("?")) {
      return url.split("?")[0];
    }

    return url;
  }

  resoveBrandSync(brandId: number | undefined | null): BrandViewModel | null {
    if (brandId == null) return null;
    if (brandId === this.brandId) return this.brand ?? null;
    if (brandId === this.specialBrandId) return this.specialBrand ?? null;
    return this.getCachedBrandById(brandId);
  }

  validateBrand(brand: string): brand is ValidTheme {
    return (themes as readonly string[]).indexOf(brand) > -1;
  }
}
